import { faCircleNotch, faInbox } from "@fortawesome/pro-regular-svg-icons";
import { Link } from "react-router-dom";

import { ExternalLink } from "src/base-components/ExternalLink";
import { CaseDeserialized } from "src/cases/queries/casesTypes";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon } from "src/design-system/Icon";
import { toastActions } from "src/design-system/Toast/utils";
import {
  getUrlToCaseQueuePage,
  getUrlToCaseTemplatesPage,
  getUrlToCaseTemplateVersionPage,
  getUrlToCaseTemplateVersionsPage,
  getUrlToSettingsPage,
} from "src/router/urls";
import { SettingsSubpages } from "src/settings/SettingsSubpages";
import { useWorkspaceAndOrg } from "src/utils/useCurrentWorkspace";

type CaseDebugTabProps = {
  caseData: CaseDeserialized | undefined;
  isLoading: boolean;
};

type DebugRowProps = {
  label: string;
  value?: React.ReactNode;
  copyable?: string;
  dataLoc?: string;
};

const copyToClipboard = (value: string) => {
  navigator.clipboard.writeText(value);
  toastActions.success({
    title: "Copied to clipboard",
    description: value,
  });
};

const DebugRow: React.FC<DebugRowProps> = ({
  label,
  value,
  copyable,
  dataLoc,
}) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  return (
    <div
      className="flex items-start justify-between gap-3 py-1"
      data-loc={dataLoc}
    >
      <dt className="font-inter-medium-12px text-gray-500">{label}</dt>
      <dd className="break-all text-right font-inter-normal-12px text-gray-800">
        {copyable ? (
          <button
            aria-label={`Copy ${label}: ${copyable}`}
            className="rounded text-right hover:text-indigo-600"
            type="button"
            onClick={() => copyToClipboard(copyable)}
          >
            {value}
          </button>
        ) : (
          value
        )}
      </dd>
    </div>
  );
};

type SectionProps = {
  title: string;
  dataLoc?: string;
  children: React.ReactNode;
};

const Section: React.FC<SectionProps> = ({ title, dataLoc, children }) => (
  <section className="flex flex-col" data-loc={dataLoc}>
    <h2 className="mb-1 font-inter-semibold-13px text-gray-800">{title}</h2>
    <dl className="flex flex-col">{children}</dl>
  </section>
);

const IdList: React.FC<{ ids: string[]; label: string }> = ({ ids, label }) => {
  if (ids.length === 0) {
    return <span className="font-inter-normal-12px text-gray-800">0</span>;
  }

  return (
    <details className="font-inter-normal-12px text-gray-800">
      <summary className="cursor-pointer">{ids.length}</summary>
      <ul className="mt-1 flex flex-col gap-y-0.5">
        {ids.map((id) => (
          <li key={id}>
            <button
              aria-label={`Copy ${label} id`}
              className="break-all text-left hover:text-indigo-600"
              type="button"
              onClick={() => copyToClipboard(id)}
            >
              {id}
            </button>
          </li>
        ))}
      </ul>
    </details>
  );
};

export const CaseDebugTab: React.FC<CaseDebugTabProps> = ({
  caseData,
  isLoading,
}) => {
  const { workspace, org } = useWorkspaceAndOrg();

  if (isLoading) {
    return (
      <div
        className="flex h-full items-center justify-center"
        data-loc="case-debug-tab-loading"
      >
        <Icon icon={faCircleNotch} size="md" spin />
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="flex h-full" data-loc="case-debug-tab-empty">
        <EmptyState
          description="No case data available."
          headline="Nothing to debug"
          icon={faInbox}
        />
      </div>
    );
  }

  const orgId = org?.id ?? "";
  const wsId = workspace?.id ?? "";
  const caseAttrs = caseData.data;
  const caseProperties = caseAttrs.case_properties;
  const template = caseAttrs.template;
  const templateVersion = caseAttrs.template_version;
  const isStaleVersion =
    !!templateVersion?.etag &&
    !!caseAttrs.template_version_etag &&
    templateVersion.etag !== caseAttrs.template_version_etag;
  const runningActionIds = (
    caseAttrs.running_actions as unknown as Array<{ id: string }>
  ).map((action) => action.id);

  const rawJson = JSON.stringify(caseAttrs, null, 2);

  return (
    <div
      className="flex flex-col gap-y-6 p-4 font-inter-normal-12px"
      data-loc="case-debug-tab"
    >
      <Section dataLoc="case-debug-section-case" title="Case">
        <DebugRow
          copyable={caseAttrs.id}
          dataLoc="case-debug-row-case-id"
          label="ID"
          value={caseAttrs.id}
        />
        <DebugRow label="Environment" value={caseAttrs.env} />
        <DebugRow label="Status" value={caseProperties?.status} />
        <DebugRow label="Sub-status" value={caseProperties?.sub_status} />
        <DebugRow
          copyable={caseAttrs.etag}
          label="Etag"
          value={caseAttrs.etag}
        />
        <DebugRow
          label="Template schema version"
          value={caseAttrs.template_schema_version}
        />
        <DebugRow label="Created at" value={caseAttrs.created_at} />
        <DebugRow label="Updated at" value={caseAttrs.updated_at} />
        <DebugRow
          label="Queue"
          value={
            caseAttrs.queue?.id ? (
              <Link
                className="text-indigo-600 hover:underline"
                to={getUrlToCaseQueuePage({
                  orgId,
                  wsId,
                  queueId: caseAttrs.queue.id,
                })}
              >
                {caseAttrs.queue.id}
              </Link>
            ) : undefined
          }
        />
        <DebugRow label="Assignee" value={caseAttrs.assignee?.id} />
      </Section>

      {template ? (
        <Section dataLoc="case-debug-section-template" title="Template">
          <DebugRow
            copyable={template.id}
            dataLoc="case-debug-row-template-id"
            label="ID"
            value={template.id}
          />
          <DebugRow label="Name" value={template.template_name} />
          <DebugRow label="Description" value={template.description} />
          <DebugRow
            label="Versions"
            value={
              <Link
                className="text-indigo-600 hover:underline"
                to={getUrlToCaseTemplateVersionsPage(orgId, wsId, template.id)}
              >
                Open versions
              </Link>
            }
          />
          <DebugRow
            label="All templates"
            value={
              <Link
                className="text-indigo-600 hover:underline"
                to={getUrlToCaseTemplatesPage(orgId, wsId)}
              >
                Open templates
              </Link>
            }
          />
        </Section>
      ) : null}

      {templateVersion ? (
        <Section
          dataLoc="case-debug-section-template-version"
          title="Template version"
        >
          <DebugRow
            copyable={templateVersion.id}
            dataLoc="case-debug-row-template-version-id"
            label="ID"
            value={templateVersion.id}
          />
          <DebugRow label="Name" value={templateVersion.version_name} />
          <DebugRow
            label="Publish state"
            value={templateVersion.publish_state}
          />
          <DebugRow
            label="Schema version"
            value={templateVersion.schema_version}
          />
          <DebugRow label="Published at" value={templateVersion.published_at} />
          <DebugRow
            label="Archived"
            value={templateVersion.archived ? "yes" : "no"}
          />
          <DebugRow
            copyable={templateVersion.etag}
            label="Version etag"
            value={templateVersion.etag}
          />
          <DebugRow
            copyable={caseAttrs.template_version_etag}
            label="Case's pinned etag"
            value={
              <span className="inline-flex items-center gap-1">
                {caseAttrs.template_version_etag}
                {isStaleVersion ? (
                  <span
                    className="rounded bg-yellow-100 px-1.5 py-0.5 font-inter-medium-12px text-yellow-700"
                    data-loc="case-debug-stale-badge"
                  >
                    Stale
                  </span>
                ) : null}
              </span>
            }
          />
          {template ? (
            <DebugRow
              label="Open"
              value={
                <Link
                  className="text-indigo-600 hover:underline"
                  to={getUrlToCaseTemplateVersionPage(
                    orgId,
                    wsId,
                    template.id,
                    templateVersion.id,
                  )}
                >
                  Open version
                </Link>
              }
            />
          ) : null}
        </Section>
      ) : null}

      <Section dataLoc="case-debug-section-composition" title="Composition">
        <DebugRow
          label="Case blocks"
          value={
            <IdList
              ids={caseAttrs.case_blocks.map((block) => block.id)}
              label="case block"
            />
          }
        />
        <DebugRow
          label="File categories"
          value={
            <IdList
              ids={caseAttrs.file_categories.map((category) => category.id)}
              label="file category"
            />
          }
        />
        <DebugRow
          label="Case tasks"
          value={
            <IdList
              ids={caseAttrs.case_tasks.map((task) => task.id)}
              label="case task"
            />
          }
        />
        <DebugRow
          label="Case actions"
          value={
            <IdList
              ids={caseAttrs.case_actions.map((action) => action.id)}
              label="case action"
            />
          }
        />
        <DebugRow
          label="Running actions"
          value={<IdList ids={runningActionIds} label="running action" />}
        />
      </Section>

      <Section dataLoc="case-debug-section-copilot" title="Copilot">
        {templateVersion?.copilot_settings ||
        templateVersion?.copilot_reference ? (
          <>
            {templateVersion.copilot_reference ? (
              <div className="flex flex-col gap-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-inter-medium-12px text-gray-500">
                    Reference
                  </span>
                  <button
                    aria-label="Copy copilot reference"
                    className="font-inter-medium-12px text-indigo-600 hover:underline"
                    type="button"
                    onClick={() =>
                      copyToClipboard(
                        JSON.stringify(
                          templateVersion.copilot_reference,
                          null,
                          2,
                        ),
                      )
                    }
                  >
                    Copy
                  </button>
                </div>
                <pre className="overflow-x-auto rounded bg-gray-50 p-2 text-gray-800">
                  {JSON.stringify(templateVersion.copilot_reference, null, 2)}
                </pre>
              </div>
            ) : null}
            {templateVersion.copilot_settings ? (
              <div className="mt-2 flex flex-col gap-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-inter-medium-12px text-gray-500">
                    Settings
                  </span>
                  <button
                    aria-label="Copy copilot settings"
                    className="font-inter-medium-12px text-indigo-600 hover:underline"
                    type="button"
                    onClick={() =>
                      copyToClipboard(
                        JSON.stringify(
                          templateVersion.copilot_settings,
                          null,
                          2,
                        ),
                      )
                    }
                  >
                    Copy
                  </button>
                </div>
                <pre className="overflow-x-auto rounded bg-gray-50 p-2 text-gray-800">
                  {JSON.stringify(templateVersion.copilot_settings, null, 2)}
                </pre>
              </div>
            ) : null}
          </>
        ) : (
          <span className="font-inter-normal-12px text-gray-800">
            Not configured
          </span>
        )}
      </Section>

      <Section dataLoc="case-debug-section-workspace" title="Workspace">
        <DebugRow
          copyable={wsId}
          label="Workspace ID"
          value={wsId || undefined}
        />
        <DebugRow copyable={orgId} label="Org ID" value={orgId || undefined} />
        <DebugRow label="AWS region" value={workspace?.aws_region} />
        <DebugRow
          label="Powertools settings"
          value={
            orgId && wsId ? (
              <ExternalLink
                href={getUrlToSettingsPage(
                  orgId,
                  wsId,
                  SettingsSubpages.Powertools,
                )}
              >
                Open settings
              </ExternalLink>
            ) : undefined
          }
        />
      </Section>

      <Section dataLoc="case-debug-section-raw-json" title="Raw JSON">
        <details>
          <summary className="cursor-pointer font-inter-medium-12px text-gray-500">
            Show payload
          </summary>
          <div className="mt-2 flex flex-col gap-y-2">
            <button
              aria-label="Copy raw JSON"
              className="self-start rounded bg-indigo-50 px-2 py-1 font-inter-medium-12px text-indigo-700 hover:bg-indigo-100"
              type="button"
              onClick={() => copyToClipboard(rawJson)}
            >
              Copy JSON
            </button>
            <pre className="overflow-x-auto rounded bg-gray-50 p-2 text-gray-800">
              {rawJson}
            </pre>
          </div>
        </details>
      </Section>
    </div>
  );
};
